import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connectScreencast, SCREENCAST_CLOSE } from '../screencast';
import { api } from '../api';

/**
 * CDP-screencast captcha viewer.
 *
 * Replaces the cropped-noVNC iframe with a WebSocket → canvas pipe
 * that streams JPEG frames from the V3 worker's chromium tab. Pointer
 * + keyboard events on the canvas are forwarded back over the same
 * WebSocket and dispatched into the captcha tab via CDP. The seller
 * cannot interact with anything outside the tab — no DICloak UI, no
 * desktop, no other tabs.
 *
 * Sizing: BE caps Page.startScreencast at 1000x700; the canvas is
 * fixed at this size. If the screencast resizes (chromium reports
 * a different deviceWidth/Height in metadata), we resize the canvas
 * accordingly — this keeps clientX/clientY → image-pixel translation
 * always 1:1, no scale math needed for the security filter's bbox
 * gate to land correctly.
 *
 * Keyboard events: captured at the document level WHILE the canvas
 * is focused — React's onKeyDown on a <canvas> is finicky because
 * canvas isn't focusable by default. We give the canvas tabIndex=0
 * and set focus on click; document listeners then fire while focus
 * is on the canvas.
 *
 * Props:
 *   sessionId — passes to connectScreencast.
 *   onError(reason) — called when WS closes with an unrecoverable
 *                     code (4001 unauthorized, 4045 disabled). For
 *                     4040 (no publisher yet), the component shows
 *                     a "waiting" state and lets the seller retry.
 */
const CANVAS_W = 1280;
const CANVAS_H = 900;

// FE-side allowlist mirror of the BE security filter — keeps clearly
// invalid events off the wire. The BE filter is authoritative; this
// is just polite.
const ALLOWED_KEY_PREFIXES = ['Key', 'Digit', 'Numpad'];
const ALLOWED_NAMED_CODES = new Set([
  'Space', 'Enter', 'NumpadEnter',
  'Backspace',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Delete', 'Home', 'End',
  'Comma', 'Period', 'Slash',
  'Minus', 'Equal',
  'Semicolon', 'Quote', 'Backquote',
  'BracketLeft', 'BracketRight', 'Backslash',
]);
function feKeyAllowed(code, modifiers) {
  if (!code) return false;
  // Allow Shift only (bit 8); reject any Ctrl/Alt/Meta.
  if (modifiers & 7) return false;
  for (const p of ALLOWED_KEY_PREFIXES) {
    if (code.startsWith(p)) return true;
  }
  return ALLOWED_NAMED_CODES.has(code);
}

function modifiersFromEvent(e) {
  // CDP bitmask: 1=Alt, 2=Ctrl, 4=Meta, 8=Shift.
  return (e.altKey ? 1 : 0)
       | (e.ctrlKey ? 2 : 0)
       | (e.metaKey ? 4 : 0)
       | (e.shiftKey ? 8 : 0);
}

export default function StepCaptchaScreencast({ sessionId, onError }) {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const imgPoolRef = useRef([]); // Image element pool — reuse to avoid GC pressure
  // Latest screencast frame metadata. CDP `Page.startScreencast` with
  // maxWidth/maxHeight scales the JPEG down from the chromium viewport,
  // so the canvas size = JPEG size ≠ viewport size. We capture
  // `deviceWidth`/`deviceHeight` (viewport DIP, same units CDP
  // `Input.dispatchMouseEvent` expects) from each frame so we can
  // translate canvas-pixel pointer coords to viewport-pixel coords
  // before sending. Without this, every click lands at a wrong place
  // on the page and the security filter's bbox gate (also in viewport
  // coords) lets through whatever happens to coincidentally line up.
  const metadataRef = useRef({ deviceWidth: 0, deviceHeight: 0 });
  const [state, setState] = useState('connecting'); // 'connecting' | 'streaming' | 'waiting' | 'closed' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [frameCount, setFrameCount] = useState(0);
  // Becomes true if state stays 'closed' for >5s — i.e., publisher
  // stopped (captcha submitted) but the parent step's SSE handler
  // hasn't unmounted us yet, so something is wrong upstream. Gives
  // the operator an explicit recovery action instead of leaving them
  // staring at a frozen frame.
  const [stuckClosed, setStuckClosed] = useState(false);
  // When the parent's SSE handler misses an event (auto-reconnect lost
  // the message between disconnect/reconnect), we poll /status to
  // discover the real outcome — either the session has FAILED with a
  // failureReason we can show, or it's still in progress and we keep
  // waiting. Without this fallback, a dropped SSE event leaves the
  // operator staring at a generic "please refresh" hint and they
  // can't tell whether the takeover worked.
  const [terminalError, setTerminalError] = useState(null);

  const sendMessage = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(msg)); } catch (_) { /* ignore */ }
    }
  }, []);

  const drawFrame = useCallback((base64, onLoaded) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Reuse an Image element from the pool. Image.decode() would be
    // cleaner but isn't supported in all targets; the onload pattern
    // is universal.
    let img = imgPoolRef.current.pop();
    if (!img) img = new Image();
    img.onload = () => {
      try {
        if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          // Mirror the JPEG's natural ratio onto the CSS box so the
          // browser shrinks BOTH width and height proportionally when
          // `max-height` binds. Without this, `width: 100%` stays
          // pinned and only height clamps — image stretches
          // horizontally on shorter viewports (testv 2026-05-07).
          canvas.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
        }
        ctx.drawImage(img, 0, 0);
      } finally {
        // Return to pool for reuse.
        if (imgPoolRef.current.length < 4) imgPoolRef.current.push(img);
        if (onLoaded) onLoaded();
      }
    };
    img.onerror = () => {
      if (imgPoolRef.current.length < 4) imgPoolRef.current.push(img);
      if (onLoaded) onLoaded();
    };
    img.src = `data:image/jpeg;base64,${base64}`;
  }, []);

  // WS lifecycle.
  useEffect(() => {
    const sessionToken = sessionStorage.getItem('sessionToken') || '';
    if (!sessionToken) {
      setErrorMsg('Session expired. Refresh to continue.');
      setState('error');
      if (onError) onError('no_session_token');
      return;
    }

    let ws;
    try {
      ws = connectScreencast(sessionId, sessionToken);
    } catch (e) {
      setErrorMsg(e?.message || 'Could not open verification stream');
      setState('error');
      if (onError) onError('ws_open_failed');
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setState('streaming');
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (_) { return; }
      if (msg.type === 'frame' && msg.data) {
        // Capture viewport size from frame metadata for pointer-coord
        // translation. The publisher self-acks on the BE, so the
        // FE-side ack is redundant for backpressure; we keep sending
        // it for parity with the wire format.
        const meta = msg.metadata || {};
        if (meta.deviceWidth && meta.deviceHeight) {
          metadataRef.current = {
            deviceWidth: meta.deviceWidth,
            deviceHeight: meta.deviceHeight,
          };
        }
        const fid = msg.frameId;
        drawFrame(msg.data, () => {
          sendMessage({ type: 'ack', frameId: fid });
        });
        setFrameCount((c) => c + 1);
      } else if (msg.type === 'stopped') {
        setState('closed');
      }
      // 'blocked' messages are noisy info; ignore on FE.
    };

    ws.onclose = (event) => {
      const code = event.code;
      if (code === SCREENCAST_CLOSE.NO_PUBLISHER) {
        // Captcha publisher hasn't started yet OR has gone away.
        // Show a waiting state but keep the user on this screen —
        // the parent will swap us for an error if it gives up.
        setState('waiting');
        return;
      }
      if (code === SCREENCAST_CLOSE.DISABLED) {
        setErrorMsg('Verification viewer is disabled on this server.');
        setState('error');
        if (onError) onError('screencast_disabled');
        return;
      }
      if (code === SCREENCAST_CLOSE.UNAUTHORIZED) {
        setErrorMsg('Verification stream rejected your session token.');
        setState('error');
        if (onError) onError('unauthorized');
        return;
      }
      // Any other close after we'd been streaming → connection lost.
      setState((s) => (s === 'streaming' ? 'closed' : s));
    };

    ws.onerror = () => {
      // onerror fires on transport-level errors; the close event
      // follows. We don't surface here to avoid double-reporting.
    };

    return () => {
      try { ws.close(); } catch (_) { /* ignore */ }
      wsRef.current = null;
    };
  }, [sessionId, onError, drawFrame, sendMessage]);

  // Pointer events on the canvas. Three coordinate spaces in play:
  //   CSS pixels       — what `e.clientX/Y` reports, sized by the
  //                      browser layout (may be smaller/larger than
  //                      the canvas intrinsic res if CSS resizes it).
  //   canvas pixels    — the canvas element's intrinsic resolution,
  //                      which we set to the JPEG's natural size in
  //                      drawFrame.
  //   viewport CSS px  — what CDP `Input.dispatchMouseEvent` and the
  //                      BE security filter's bbox both expect. This
  //                      is the chromium device viewport size,
  //                      reported as deviceWidth/Height in screencast
  //                      frame metadata.
  // Translation: clientX/Y → canvas px (correct for CSS scaling) →
  //              viewport px (correct for screencast downscaling).
  const handleMouse = useCallback((eventType) => (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cssToCanvasX = canvas.width / rect.width;
    const cssToCanvasY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * cssToCanvasX;
    const canvasY = (e.clientY - rect.top) * cssToCanvasY;
    const meta = metadataRef.current;
    // If we haven't received a frame yet, we can't translate
    // accurately. Drop the event rather than send wrong coords.
    if (!meta.deviceWidth || !meta.deviceHeight || !canvas.width || !canvas.height) {
      return;
    }
    const x = canvasX * (meta.deviceWidth / canvas.width);
    const y = canvasY * (meta.deviceHeight / canvas.height);
    // Only forward left-click; the BE filter rejects other buttons,
    // but we drop them locally to avoid noisy `blocked` echoes.
    if (eventType !== 'mousemove' && e.button !== 0) return;
    sendMessage({
      type: 'mouse',
      eventType,
      x, y,
      button: 0,
      clickCount: eventType === 'mousedown' ? 1 : 0,
    });
    // Prevent default drag behavior on the canvas image.
    if (eventType === 'mousedown') e.preventDefault();
  }, [sendMessage]);

  // Watch for the "closed but parent hasn't advanced" stuck state,
  // and once stuck, poll /status to discover the real outcome (SSE
  // may have dropped a captcha_solved/v3_blocked event during the WS
  // session).
  useEffect(() => {
    if (state !== 'closed') {
      setStuckClosed(false);
      setTerminalError(null);
      return;
    }
    const stuckTimer = setTimeout(() => setStuckClosed(true), 5000);

    let cancelled = false;
    let pollTimer = null;
    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await api.getStatus(sessionId);
        if (cancelled) return;
        if (status?.status === 'failed') {
          setTerminalError(
            status.failureReason || 'Login failed. Please try again.',
          );
          if (onError) onError(status.failureReason || 'login_failed');
          return; // stop polling — terminal
        }
        // Still in progress (status === 'started') — keep polling
        // until parent unmounts us or we hit a terminal state.
      } catch (_) {
        // Network blip — try again next tick.
      }
      if (!cancelled) {
        pollTimer = setTimeout(poll, 4000);
      }
    };
    // First poll fires after stuckTimer fires, not immediately —
    // gives the parent's SSE-driven unmount a chance first.
    const firstPollTimer = setTimeout(poll, 5000);

    return () => {
      cancelled = true;
      clearTimeout(stuckTimer);
      clearTimeout(firstPollTimer);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [state, sessionId, onError]);

  // Keyboard events while canvas is focused. Document-level listener
  // because <canvas> doesn't natively dispatch keyboard events.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    function handleKey(eventType) {
      return (e) => {
        // Only fire when canvas owns focus.
        if (document.activeElement !== canvas) return;
        const modifiers = modifiersFromEvent(e);
        if (!feKeyAllowed(e.code, modifiers)) {
          // Don't preventDefault — let the browser handle non-allowed
          // keys normally (e.g. Tab leaves the captcha viewer).
          return;
        }
        e.preventDefault();
        sendMessage({
          type: 'key',
          keyEvent: eventType,
          code: e.code,
          text: e.key && e.key.length === 1 ? e.key : '',
          key: e.key || '',
          modifiers,
        });
      };
    }
    const onDown = handleKey('keydown');
    const onUp = handleKey('keyup');
    document.addEventListener('keydown', onDown);
    document.addEventListener('keyup', onUp);
    return () => {
      document.removeEventListener('keydown', onDown);
      document.removeEventListener('keyup', onUp);
    };
  }, [sendMessage]);

  return (
    <div style={containerStyle}>
      {/* If frames have already streamed, treat 'waiting' identically
          to stuck-closed: the publisher is gone, the captcha is over,
          we should show failure not a stale-frame canvas. The only
          'waiting' state where we still show the canvas is the
          *initial* connect, before any frame has arrived (frameCount
          === 0). */}
      {state === 'connecting' && (
        <p style={messageStyle}>Connecting to verification challenge…</p>
      )}
      {state === 'waiting' && frameCount === 0 && (
        <p style={messageStyle}>
          Waiting for the verification challenge to load. This usually takes a few seconds.
        </p>
      )}
      {/* Failure surface — hide the canvas, show a single red message.
          Triggered by:
            • state='closed' AND terminalError (poll caught a real
              failureReason from /status), OR
            • state='closed' AND stuckClosed (5s+ since WS close, no
              parent unmount), OR
            • state='waiting' AND we already streamed frames (the
              publisher disappeared mid-flow; testv38 2026-05-07
              showed the LinkedIn login page in the stale canvas
              while the BE had already failed at login). */}
      {((state === 'closed' && (terminalError || stuckClosed))
        || (state === 'waiting' && frameCount > 0)) && (
        <p style={{ ...messageStyle, color: 'var(--danger, #ef4444)' }}>
          {terminalError
            || 'Login failed. Please try again and double-check your email and password.'}
        </p>
      )}
      {/* B-CAPTCHA-FE-SURFACING (2026-05-30): the "Verification submitted —
          completing login…" banner used to render here on EVERY transient
          'closed' state. The screencast WS closes+reconnects during an active
          captcha (closed↔streaming), so this single line flashed on/off ABOVE
          the canvas and bounced the whole captcha section up/down. The canvas +
          "Please complete…" copy below stay stable across streaming/closed-not-
          stuck, so dropping this toggling banner removes the jitter; the
          envelope clearing the captcha drives the post-solve transition (the
          recovering/next screen takes over). */}
      {state === 'error' && (
        <p style={{ ...messageStyle, color: 'var(--danger, #ef4444)' }}>
          {errorMsg || 'Could not open the verification challenge.'}
        </p>
      )}
      {/* Canvas mounted: streaming, fresh-waiting (pre-first-frame),
          or pre-stuck closed. Hidden once we surface failure. */}
      {(state === 'streaming'
        || (state === 'waiting' && frameCount === 0)
        || (state === 'closed' && !stuckClosed && !terminalError)) && (
        <>
          <p style={messageStyle}>
            Please complete the security challenge below.
            {frameCount === 0 && state === 'streaming' && ' Loading first frame…'}
          </p>
          <canvas
            ref={canvasRef}
            tabIndex={0}
            width={CANVAS_W}
            height={CANVAS_H}
            onClick={(e) => { e.currentTarget.focus(); }}
            onMouseDown={handleMouse('mousedown')}
            onMouseUp={handleMouse('mouseup')}
            onMouseMove={handleMouse('mousemove')}
            onContextMenu={(e) => e.preventDefault()}
            style={canvasStyle}
            aria-label="Verification challenge"
          />
        </>
      )}
    </div>
  );
}

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  padding: '16px',
  // Stretch to full viewport width on every form factor. The 640px
  // wizard column is too narrow for the captcha — escape it via the
  // negative-margin trick, then use 16px padding inside the wrapper
  // so the canvas has a small gutter from the screen edges on mobile.
  // The canvas itself caps at 1500 wide (canvasStyle.maxWidth) so
  // wide desktops don't get a comically large widget.
  width: '100vw',
  marginLeft: 'calc(50% - 50vw)',
  marginRight: 'calc(50% - 50vw)',
  boxSizing: 'border-box',
};

const messageStyle = {
  color: 'var(--text)',
  fontSize: 14,
  textAlign: 'center',
  margin: 0,
  maxWidth: 720,
};

const canvasStyle = {
  display: 'block',
  margin: '0 auto',
  // Aspect-preserving sizing on every viewport.
  //
  // `width: auto + height: auto` lets the canvas display at its
  // intrinsic dimensions (driven by the canvas's HTML width/height
  // attributes, kept in sync with the JPEG's natural size by
  // drawFrame). With `max-width: 100%` and `max-height: calc(100vh
  // - 80px)`, CSS3's "transferred sizing" applies the intrinsic
  // ratio: when EITHER cap binds, BOTH dimensions shrink
  // proportionally to preserve aspect.
  //
  // Why not `width: 100%`?  PR #190 used it to fill the viewport
  // horizontally on desktop, but Chrome treats `width: 100%` as a
  // definite size and then clamps only height when `max-height`
  // binds — leaving the CSS box wider-than-aspect and stretching
  // the JPEG horizontally on shorter desktop viewports (operator
  // screenshot 2026-05-07 testv after PR #190 deploy). Mobile didn't
  // hit the bug because narrow parent's max-width clamp ran first,
  // re-deriving height through transferred sizing correctly.
  //
  // Sizing on common viewports (assuming chromium emits 1280:791):
  //   • Mobile portrait (375×667):  343×212  — max-width clamps
  //     width down, height transferred via aspect.
  //   • Laptop (1366×768):          1113×688 — max-height clamps
  //     height down, width transferred via aspect.
  //   • Desktop (1920×1080):        1280×791 — intrinsic size,
  //     centered in the wider container.
  width: 'auto',
  height: 'auto',
  aspectRatio: '1280 / 900',
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 80px)',
  border: '1px solid var(--border, #ddd)',
  borderRadius: 8,
  background: '#000',
  cursor: 'crosshair',
  outline: 'none',
};
